import React, { ChangeEvent, FormEvent, SyntheticEvent, useState, MouseEvent, useEffect } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import {
    CustomerInfo,
    ShippingLabelTypes,
    ShippingPortalLabelSizes,
    ShippingPortalLocation,
    ShippingPortalParcel,
    UserPreferences,
    NestedKeyOf,
    Permissions,
} from '@lanterds/types';
import { getPreferences, updateOutboundPreferences, generateOutboundParcelPdfs } from 'src/api/shippingPortal';
import {
    Autocomplete,
    Box,
    CircularProgress,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    Button,
    Card,
    CardActions,
    CardContent,
    FormControl,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import './OutboundLabels.scss';
import Tabs from 'src/components/common/Tabs/Tabs';
import { getUserEmailFromToken } from 'src/util/helpers';
import { DealerContainer } from './DealerContainer';
import { openPDFInNewTab } from 'src/util/openPdf';
import Favorites from './Favorites';
import dayjs from 'dayjs';
import { getLocationById } from 'src/api/locations';
import { OutboundTabs, LocationTypes } from '../types';
import { AddressPicker } from 'src/components/common/AddressPicker/AddressPicker';
import { allCustomersDatum, allCustomersValue, PAGE_INFO } from 'src/util/constants';
import Header from 'src/components/common/Header/Header';
import { DebouncedTextField } from 'src/components/common/DebouncedInputs/DebouncedTextField';
import { isAuthorized } from 'src/util/Permissions';
import { useLayoutContainerContext } from 'src/components/Layouts/LayoutContainer/LayoutContext';

export type OutboundShippingPortalForm = {
    customer: { label: string; id: string } | null;
    origin: ShippingPortalLocation | null;
    destinations: ShippingPortalLocation[] | null;
    quantities: Record<string, number>;
    quantitiesForAll: number;
    labelSize: ShippingPortalLabelSizes;
    shippingType: ShippingLabelTypes;
};

export type OutboundLabelsProps = {
    customers: Array<CustomerInfo>;
    customersAreLoading: boolean;
    selectedCustomer: { id: string; label: string } | null;
    setSelectedCustomer: (value: { id: string; label: string } | null) => void;
    shippingType: ShippingLabelTypes;
};

export default function OutboundLabels({
    customers,
    customersAreLoading,
    selectedCustomer,
    setSelectedCustomer,
    shippingType,
}: OutboundLabelsProps) {
    const { setIsAutoFillHeightDisabled } = useLayoutContainerContext();
    const userId = getUserEmailFromToken();
    const isUnrestrictedSearchAuthorized = isAuthorized(userId, Permissions.shippingPortal_unfilteredDestinations);

    const blankForm = {
        customer: null,
        origin: null,
        destinations: null,
        quantities: {},
        quantitiesForAll: 1,
        labelSize: ShippingPortalLabelSizes.LARGE,
        shippingType: ShippingLabelTypes.Outbound,
    } as OutboundShippingPortalForm;

    const [formValue, setFormValue] = useState<OutboundShippingPortalForm>({ ...blankForm });
    const [createNewParcels, setCreateNewParcels] = useState(true);
    const [preferences, setPreferences] = useState<UserPreferences | null>(null);
    const [currentParcels, setCurrentParcels] = useState<ShippingPortalParcel[]>([]);
    const [isPrinting, setIsPrinting] = useState(false);
    const [filterByOrigin, setFilterByOrigin] = useState(true);

    const tabLabels = ['Schedule a Shipment', 'Favorites'];
    const setAsDefaultText = 'Set As Default';
    const quantityAllId = 'quantity-all';

    const [selectedTab, setSelectedTab] = useState(0);

    const { data: userPreferences, isFetching: isFetchingUserPreferences, refetch } = getPreferences(userId);
    const { mutateAsync: updatePreferences, isPending: isUpdatingPreferences } = updateOutboundPreferences(userId);

    useEffect(() => {
        if (selectedTab === OutboundTabs.ScheduleAShipment) {
            refetch();
            setIsAutoFillHeightDisabled(true);
        } else {
            setIsAutoFillHeightDisabled(false);
        }
        return () => {
            // Reset the context when the component unmounts
            setIsAutoFillHeightDisabled(false);
        };

        return () => {
            // Reset the context when the component unmounts
            setIsAutoFillHeightDisabled(false);
        };
    }, [selectedTab]);

    useEffect(() => {
        if (userPreferences) {
            if (!userPreferences.error) {
                setPreferences(userPreferences.preferences);
            }
        }
    }, [userPreferences, isFetchingUserPreferences]);

    useEffect(() => {
        let tempFormValue = { ...formValue };

        if (preferences && preferences.outboundLabels && preferences.outboundLabels.labelSize) {
            tempFormValue = { ...tempFormValue, labelSize: preferences.outboundLabels.labelSize };
        }

        if (customers && preferences && preferences.outboundLabels && preferences.outboundLabels.customerId) {
            const foundCustomer = customers.find((customer) => customer.customerCode === preferences.outboundLabels.customerId);
            if (foundCustomer) {
                const customer = { label: foundCustomer.customerName, id: foundCustomer.customerId };
                tempFormValue = {
                    ...tempFormValue,
                    customer,
                };
                setSelectedCustomer(customer);
            }
        }

        const getLocations = async () => {
            if (preferences && preferences.outboundLabels && preferences.outboundLabels.originId) {
                const origin = await getLocationById(preferences.outboundLabels.originId);
                if (origin) {
                    tempFormValue = { ...tempFormValue, origin };
                }
            }

            if (preferences && preferences.outboundLabels && preferences.outboundLabels.destinationIds) {
                const destinations: ShippingPortalLocation[] = await Promise.all(
                    preferences.outboundLabels.destinationIds.map((id) => {
                        tempFormValue.quantities[id] = 1;
                        return getLocationById(id);
                    }),
                );
                if (destinations) {
                    tempFormValue = { ...tempFormValue, destinations: destinations };
                }
            }

            setFormValue(tempFormValue);
        };

        if (selectedTab === OutboundTabs.ScheduleAShipment) {
            getLocations();
        }
    }, [customers, preferences, selectedTab]);

    const onTabChange = (event: MouseEvent<HTMLLIElement | HTMLButtonElement> | undefined, createNew: boolean) => {
        if (event) {
            setCreateNewParcels(createNew);
            setSelectedTab(event?.currentTarget?.tabIndex);
            resetForm();
        }
    };

    const resetForm = () => {
        setCreateNewParcels(true);
        setCurrentParcels([]);
        setFormValue(blankForm);
        setSelectedCustomer(allCustomersValue);
    };

    const handleReset = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        (event.currentTarget as HTMLFormElement).reset();

        resetForm();
    };

    const handleSubmit = (event: SyntheticEvent) => {
        event.preventDefault();
        createParcelsCopies(formValue.quantities);
        setCreateNewParcels(false);
    };

    const printLabels = async (parcelsToGenerate: ShippingPortalParcel[]) => {
        setIsPrinting(true);

        generateOutboundParcelPdfs(parcelsToGenerate).then((response: { data: { pdfBase64: string } }) => {
            if (response.data.pdfBase64) openPDFInNewTab(response.data.pdfBase64);
            setIsPrinting(false);
        });
    };

    const updateCurrentParcels = (index: number, updatedParcel: ShippingPortalParcel) => {
        const tempParcels = [...currentParcels];
        tempParcels[index] = updatedParcel;
        setCurrentParcels(tempParcels);
    };

    const createParcelsCopies = (quantities: Record<string, number>) => {
        const parcelsToGenerate = [];

        if (formValue.destinations) {
            for (const location of formValue.destinations) {
                for (let i = 0; i < quantities[location.id]; i++) {
                    parcelsToGenerate.push({
                        id: '-1',
                        manifest: null,
                        parcelNumber: '-1',
                        weight: 0,
                        weightUnit: 'lbs',
                        reference: '',
                        shippingType: ShippingLabelTypes.Outbound,
                        hazardous: 'N',
                        fmtpCustomerId: location.fmtpCustomerId,
                        numberOfPieces: 1,
                        partNumber: '',
                        parcelType: 'LOOSE',
                        labelSize: formValue.labelSize,
                        createdOn: dayjs().toISOString(),
                        createdBy: userId,
                        destination: location,
                        origin: formValue.origin ?? ({} as ShippingPortalLocation),
                    });
                }
            }

            setCurrentParcels(parcelsToGenerate);
        }
    };

    const onLocationChangeInput = (locations: ShippingPortalLocation | ShippingPortalLocation[] | null, name: string) => {
        if (Array.isArray(locations)) {
            const tempQuantities: Record<string, number> = {};
            for (const location of locations) {
                tempQuantities[location.id] = 1;
            }
            setFormValue({ ...formValue, destinations: locations, quantities: tempQuantities });
        } else {
            setFormValue({ ...formValue, [name]: locations });
        }

        if (name === LocationTypes.Origin) {
            setCurrentParcels([]);
        }
    };

    const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const { name, value } = e.target;

        setFormValue({ ...formValue, [name]: value });
    };

    const handleCustomerChange = (value: { label: string; id: string } | null) => {
        if (value) {
            setSelectedCustomer(value);
            setFormValue({
                ...formValue,
                customer: value.id !== allCustomersDatum.customerId ? value : null,
                origin: null,
                destinations: null,
                quantities: {},
            });
        } else {
            setSelectedCustomer(allCustomersValue);
            setFormValue({
                ...formValue,
                customer: null,
                origin: null,
                destinations: null,
                quantities: {},
            });
        }

        setCurrentParcels([]);
    };

    const handleQuantityChange = (name: string, value: string, quantityAll?: boolean) => {
        const tempQuantities = { ...formValue.quantities };

        if (quantityAll) {
            for (const key of Object.keys(tempQuantities)) {
                tempQuantities[key] = Number(value);
            }
            setFormValue({ ...formValue, quantities: tempQuantities, quantitiesForAll: Number(value) });
        } else {
            tempQuantities[name] = Number(value);
            setFormValue({ ...formValue, quantities: tempQuantities });
        }

        createParcelsCopies(tempQuantities);
    };

    const setDefault = async (type: string, value: string | string[] | ShippingPortalLabelSizes | null) => {
        const { outboundLabels = {} } = preferences ?? {};

        await updatePreferences({
            [`outboundLabels.${type}`]: value,
        } as Record<NestedKeyOf<UserPreferences>, string | string[] | ShippingPortalLabelSizes | undefined>);

        setPreferences({
            ...preferences,
            outboundLabels: {
                ...outboundLabels,
                [type]: value,
            },
        });
    };

    const applyToAllParcels = (name: string, value: string | number) => {
        setCurrentParcels(
            currentParcels.map((parcel) => {
                return { ...parcel, [name]: value };
            }),
        );
    };

    const getHeaderText = () => {
        const subPage = PAGE_INFO.shippingPortal.subPages?.find((page) => page.name === shippingType);
        if (subPage) {
            return subPage.displayText;
        } else {
            return '';
        }
    };

    const onFilterByOriginChange = (_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setFilterByOrigin(checked);
    };

    return (
        <Box id="shipping-portal-container" className={`${selectedTab === OutboundTabs.Favorites ? 'auto-fill-height' : ''}`}>
            <Card id="outbound-labels-card" className={`${selectedTab === OutboundTabs.Favorites ? 'auto-fill-height' : ''}`}>
                <Header text={getHeaderText()} />
                <Box
                    sx={{
                        borderBottom: '1px solid #ccc',
                    }}
                    className="tabs-container"
                >
                    <Tabs
                        labels={tabLabels}
                        onClick={(event) => onTabChange(event, createNewParcels)}
                        selectedIndex={selectedTab}
                    />
                </Box>
                {selectedTab === OutboundTabs.ScheduleAShipment && (
                    <>
                        <form onReset={handleReset} onSubmit={handleSubmit} autoComplete="off">
                            <CardContent>
                                <Grid container spacing={2}>
                                    {customers.length > 1 && (
                                        <>
                                            <Grid sm={4} xs={12}>
                                                <FormControl fullWidth>
                                                    <Autocomplete
                                                        id="customerId"
                                                        options={customers.map((customer) => ({
                                                            label: customer.customerName,
                                                            id: customer.customerId,
                                                        }))}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                variant="outlined"
                                                                label="Customer"
                                                                name="customerId"
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {customersAreLoading ? (
                                                                                <CircularProgress color="inherit" size={20} />
                                                                            ) : null}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                                InputLabelProps={{ ...params.InputLabelProps }}
                                                            />
                                                        )}
                                                        value={selectedCustomer}
                                                        onChange={(event, value) => handleCustomerChange(value)}
                                                        disabled={customersAreLoading}
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid xs={12}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    className="default-button"
                                                    onClick={() => setDefault('customerId', formValue.customer?.id ?? null)}
                                                >
                                                    {setAsDefaultText}
                                                </Button>
                                            </Grid>
                                        </>
                                    )}

                                    <Grid sm={4} xs={12} container spacing={2}>
                                        <Grid xs={12} container spacing={2} className="origin-label-container">
                                            <Grid xs={isUnrestrictedSearchAuthorized ? 2 : 12}>
                                                <Typography>Origin</Typography>
                                            </Grid>
                                            {isUnrestrictedSearchAuthorized && (
                                                <Grid xs={6} className="filter-by-origin-container">
                                                    <FormControl>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={filterByOrigin}
                                                                    onChange={onFilterByOriginChange}
                                                                    disableRipple
                                                                />
                                                            }
                                                            label="Filter By Origin"
                                                            checked={filterByOrigin}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            )}
                                        </Grid>
                                        <AddressPicker
                                            name={LocationTypes.Origin}
                                            onChangeInput={onLocationChangeInput}
                                            locations={formValue.origin ? [formValue.origin] : []}
                                            context={{
                                                locationType: LocationTypes.Origin,
                                                searchType: formValue.shippingType,
                                                destinations: formValue.destinations,
                                                labelType: formValue.shippingType,
                                                origin: formValue.origin,
                                                filterByOrigin: filterByOrigin,
                                            }}
                                            enableAutoPopulate={userPreferences && !isFetchingUserPreferences}
                                            customer={formValue.customer}
                                            fieldLabels={{ code: 'Dock Code' }}
                                        />
                                        <Grid xs={12}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                className="default-button"
                                                onClick={() => setDefault('originId', formValue.origin?.id ?? null)}
                                            >
                                                {setAsDefaultText}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <Grid sm={4} xs={12} container spacing={2}>
                                        <FormControl fullWidth>
                                            <Grid xs={12}>
                                                <Typography>Destination</Typography>
                                            </Grid>
                                            <AddressPicker
                                                name={LocationTypes.Destination}
                                                onChangeInput={onLocationChangeInput}
                                                locations={formValue.destinations ?? []}
                                                context={{
                                                    locationType: LocationTypes.Destination,
                                                    searchType: formValue.shippingType,
                                                    origin: formValue.origin,
                                                    labelType: formValue.shippingType,
                                                    destinations: formValue.destinations,
                                                    filterByOrigin: filterByOrigin,
                                                }}
                                                enableAutoPopulate={userPreferences && !isFetchingUserPreferences}
                                                customer={formValue.customer}
                                                multiSelect={true}
                                            />
                                        </FormControl>
                                        <Grid xs={12}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                className="default-button"
                                                onClick={() =>
                                                    setDefault(
                                                        'destinationIds',
                                                        formValue.destinations?.map((destinations) => destinations?.id) ?? null,
                                                    )
                                                }
                                            >
                                                {setAsDefaultText}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <Grid sm={4} xs={12} container spacing={2}>
                                        {/* Using empty grid to help align content */}
                                        <Grid xs={12}>&nbsp;</Grid>
                                        <Grid sm={7} xs={12} className="apply-quantity-for-all">
                                            <Typography>Apply Quantity For All Endpoints</Typography>
                                        </Grid>
                                        <Grid sm={5} xs={12}>
                                            <FormControl fullWidth>
                                                <DebouncedTextField
                                                    id={quantityAllId}
                                                    type="number"
                                                    variant="outlined"
                                                    name={quantityAllId}
                                                    onDebounceChange={(value) =>
                                                        handleQuantityChange(quantityAllId, value, true)
                                                    }
                                                    placeholder="1"
                                                    InputProps={{
                                                        inputProps: { min: 1 },
                                                    }}
                                                    debounceValue={formValue.quantitiesForAll.toString()}
                                                    disabled={!formValue.destinations?.length}
                                                />
                                            </FormControl>
                                        </Grid>

                                        <Grid xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel id="quantities-select-label" shrink={false}>
                                                    Select to change quantity...
                                                </InputLabel>
                                                <Select
                                                    labelId="quantities-select-label"
                                                    variant="outlined"
                                                    fullWidth
                                                    MenuProps={{
                                                        className: 'apply-all-quantities-menu',
                                                        MenuListProps: {
                                                            disabledItemsFocusable: false,
                                                        },
                                                    }}
                                                    disabled={!formValue.destinations?.length}
                                                >
                                                    {formValue &&
                                                        formValue.destinations &&
                                                        formValue.destinations.map((option) => (
                                                            <MenuItem key={option.id} disableRipple>
                                                                <Box
                                                                    className="menu-item-container"
                                                                    onClick={(event) => event.stopPropagation()} // Prevent closing
                                                                >
                                                                    <Typography className="menu-item-label">
                                                                        {option.name}
                                                                    </Typography>
                                                                    <DebouncedTextField
                                                                        name={option.id}
                                                                        type="number"
                                                                        variant="outlined"
                                                                        onDebounceChange={(value) =>
                                                                            handleQuantityChange(option.id, value)
                                                                        }
                                                                        onClick={(event) => event.stopPropagation()} // Prevent closing
                                                                        onFocus={(event) => event.stopPropagation()} // Prevent hover over styles
                                                                        fullWidth
                                                                        debounceValue={formValue.quantities[
                                                                            option.id
                                                                        ].toString()}
                                                                        InputProps={{ inputProps: { min: 1 } }}
                                                                    />
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel id="size-select-label">Label Size</InputLabel>
                                                <Select
                                                    labelId="size-select-label"
                                                    name="labelSize"
                                                    id="label-size"
                                                    value={formValue.labelSize}
                                                    variant="outlined"
                                                    label="Label Size"
                                                    onChange={onChangeInput}
                                                >
                                                    <MenuItem disableRipple value={ShippingPortalLabelSizes.LARGE}>
                                                        {ShippingPortalLabelSizes.LARGE}
                                                    </MenuItem>
                                                    <MenuItem disableRipple value={ShippingPortalLabelSizes.MEDIUM}>
                                                        {ShippingPortalLabelSizes.MEDIUM}
                                                    </MenuItem>
                                                    <MenuItem disableRipple value={ShippingPortalLabelSizes.SMALL}>
                                                        {ShippingPortalLabelSizes.SMALL}
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid xs={12}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                className="default-button"
                                                onClick={() => setDefault('labelSize', formValue.labelSize)}
                                            >
                                                {setAsDefaultText}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid xs={12} className="actions-container">
                                    <CardActions>
                                        <Button variant="outlined" type="reset">
                                            Reset
                                        </Button>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            size="medium"
                                            disabled={
                                                !formValue.origin || !(formValue.destinations && formValue.destinations.length)
                                            }
                                        >
                                            Next
                                        </Button>
                                    </CardActions>
                                </Grid>
                            </CardContent>
                        </form>
                        {!createNewParcels && currentParcels.length ? (
                            <Box className="dealer-container">
                                <DealerContainer
                                    parcels={currentParcels}
                                    printLabels={printLabels}
                                    showFavoritesDetails={false}
                                    handleReset={resetForm}
                                    updateCurrentParcels={updateCurrentParcels}
                                    isPrinting={isPrinting}
                                    applyToAllParcels={applyToAllParcels}
                                    setSelectedTab={setSelectedTab}
                                />
                            </Box>
                        ) : (
                            <></>
                        )}
                    </>
                )}
                {selectedTab === OutboundTabs.Favorites && (
                    <Favorites
                        generatedParcels={currentParcels}
                        printLabels={printLabels}
                        onTabChange={onTabChange}
                        isPrinting={isPrinting}
                    />
                )}
            </Card>
        </Box>
    );
}
